import { EventEmitter } from 'events';
import RecorderServer from './services/RecorderServer';
import StreamRecorder from './modules/Recorder';
import Debug from './debug';

class UploadBuffer {
    constructor() {
        this.buffer = new Map();
        this.currentChunkId = 0;
        this.chunkToRead = null;
    }

    getNext() {
        if (!this.chunkToRead) this.chunkToRead = this.currentChunkId;
        if (this.buffer.size === 0) return;
        return this.buffer.get(this.chunkToRead);
    }

    removeLastRead() {
        this.buffer.delete(this.chunkToRead);
        this.chunkToRead++;
    }

    addChunk(blob) {
        if (!(blob instanceof Blob)) return;
        this._addToBuffer(blob);
    }

    complete() {
        this._addToBuffer(new Blob(), true);
    }

    _addToBuffer(blob, complete = false) {
        this.currentChunkId++;

        let obj = {
            chunkId: this.currentChunkId,
            chunk: blob
        };

        if (complete) obj.complete = true;

        this.buffer.set(this.currentChunkId, obj); 
    }
}

export default class RecordingController extends EventEmitter {
    constructor() {
        super();

        this.recorder = null;

        this.server = new RecorderServer({
            name: 'Recorder Server',
            server: process.env.VUE_APP_RECORD_SERVER,
            path: process.env.VUE_APP_RECORD_PATH
        });

        this.connected = false;
        this.token = null;
        this.sessionId = null;
        this.recordingId = null;
        this.inputStream = null;

        this.isRecording = false;
        this.buffer = null;
        this.sendLock = false;
    }

    connect(token) {
        return new Promise((resolve, reject) => {
            this.server.connect(token);

            this.server.on('DISCONNECTED', () => {
                this.connected = false;
                reject(new Error('Disconnected'));
            });

            this.server.on('AUTHORISED', () => {
                this.connected = true;
                resolve();
            });

            this.server.on('UNAUTHORISED', () => {
                this.connected = false;
                reject(new Error('Unauthorised'));
            })
        });
    }

    async start(config) {
        if (this.isRecording) return this.recordingId; //throw new Error('Already recording');
        
        this.sessionId = config.sessionId || null;
        this.stream = config.stream || null;

        if (!this.sessionId) throw new Error('No session ID');
        if (!this.stream) throw new Error('No MediaStream set');

        this.buffer = new UploadBuffer();

        this.recorder = new StreamRecorder({
                stream: this.stream,
                debug: Debug('bbc:Recorder')
            });

        this._init();
        
        this.recordingId = await this.server.startRecording(this.sessionId);
        this.recorder.start();

        return this.recordingId;
    }

    stop() {
        if (this.recorder) this.recorder.stop();
    }

    forceComplete(recordingId) {
        return this._stopServer(recordingId, true);
    }

    cutAudio(b) {
        if (this.recorder) {
            this.recorder.cutAudio(b);
        }
    }

    _init() {
        this.recorder.on('start', () => {                 // Local Recording has STARTED
            this.isRecording = true;
            this.emit('RECORDING', true);
        });

        this.recorder.on('stop', () => {                  // Local Recording has STOPPED
            this._stopServer(this.recordingId, this.stoppedOnError);

            this.buffer.complete();
            this._sendChunk();

            this.isRecording = false;
            this.emit('RECORDING', false);
        });

        this.recorder.on('chunk', (data) => {               // NEW audio data, add it to the buffer
            this.buffer.addChunk(data);
            this._sendChunk();
            // console.log('Buffer size:', this.buffer.size);
        });
    }

    _stopServer(recordingId, force = false) {
        let waitForChunk = force ? 0 : (this.buffer.currentChunkId + 1);
        return this.server.stopRecording(recordingId, waitForChunk);
    }

    _getNextChunk() {
        return this.buffer.getNext();
    }

    _chunkSentSuccess() {
        this.lastChunkReceivedTime = Date.now();
        this.buffer.removeLastRead();
    }

    async _sendChunk() {
        if (this.sendLock) return;  // only allow one chunk to be sent at a time
        if (!this.connected) return; // don't try and send chunk if we aren't connected to server

        let chunkData = this.buffer.getNext();
        if (!chunkData) return;

        try {
            this.sendLock = true;


            chunkData.recordingId = this.recordingId;
            let result = await this.server.sendChunk(chunkData);

            this.sendLock = false;

            if (result.success === true) {
                this.emit('CHUNK_UPLOAD_SUCCESS');
                this._chunkSentSuccess();
                this._sendChunk();    // check if there are more chunks in the buffer
            }
            else {
                if (result.expected) {
                    console.warn(`Expected chunk ${result.expected}. Should we start a new recording on server?`);
                }
                this.stop(true);    // stopped on error

                this.emit('RECORDING_FAILED', {
                    lastChunkTime: this.lastChunkReceivedTime
                });

                console.error(`RECORDING FAILED. Last chunk received ${(Date.now() - this.lastChunkReceivedTime) / 1000} seconds ago`);
            }
        }
        catch (err) {
            this.emit('CHUNK_UPLOAD_FAIL');
            this.sendLock = false;
            console.error('Unable to upload chunk', err.message);
        }
    }
}